import { useCallback, useEffect, useMemo } from "react";
import isEmpty from "lodash/isEmpty";
import { useCurrentUser } from "./useCurrentUser";
import { useUsers } from "./useUsers";

type Snapshot = {
  get: (path: string) => any;
  data?: () => any;
  id?: string;
};

type Option = {
  label: string;
  value: string;
};

export const useProtocolAuthorFieldState = (
  object: Snapshot,
  updateObject: (data: any) => Promise<void>
) => {
  const [currentUser, isCurrentUserLoading] = useCurrentUser();
  const [users] = useUsers();

  const protocolAuthor = object.get("protocolAuthor") ?? "";
  const protocolAuthorRef = (object.get("protocolAuthorRef") as string) ?? "";

  const options = useMemo<Option[]>(() => {
    const userOptions = users
      .filter((u: any) => u.data().isActive === true)
      .map((u: any) => ({
        label: (u.data().name as string) || (u.id as string),
        value: u.id,
      }))
      .sort((a: Option, b: Option) => a.label.localeCompare(b.label, "de"));

    return [{ label: "— keine Auswahl —", value: "" }, ...userOptions];
  }, [users]);

  const usersById = useMemo(() => {
    const map = new Map<string, any>();
    users.forEach((u: any) => map.set(u.id, u));
    return map;
  }, [users]);

  const updateWithSnapshot = useCallback(
    async (data: any) => {
      if ("protocolAuthorRef" in data) {
        const userId = data.protocolAuthorRef as string;
        if (!userId) {
          return updateObject({ protocolAuthorRef: "" });
        }
        const picked = usersById.get(userId);
        const snapshot = picked?.data()?.protocolAuthor ?? "";
        return updateObject({
          protocolAuthorRef: userId,
          protocolAuthor: snapshot,
        });
      }
      return updateObject(data);
    },
    [updateObject, usersById]
  );

  useEffect(() => {
    if (isCurrentUserLoading) return;
    if (!isEmpty(protocolAuthorRef) || !isEmpty(protocolAuthor)) return;

    const currentUserId = (currentUser as any)?.id;
    const userProtocolAuthor = (currentUser as any)?.get?.("protocolAuthor");
    if (!currentUserId || isEmpty(userProtocolAuthor)) return;

    updateObject({
      protocolAuthorRef: currentUserId,
      protocolAuthor: userProtocolAuthor,
    });
  }, [
    isCurrentUserLoading,
    currentUser,
    protocolAuthor,
    protocolAuthorRef,
    updateObject,
  ]);

  return { options, updateWithSnapshot, protocolAuthorRef };
};
